import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Sparkles, LayoutGrid, SlidersHorizontal, TimerReset, PlusCircle, Link as LinkIcon } from 'lucide-react';
import { PageBackLink, PageShell } from '../MarketingPageShell';

const featureCategories = [
  {
    title: 'Productivity Suite',
    description: 'A collection of practical desktop tools designed to stay fast, visible, and useful throughout the day.',
    features: [
      {
        name: 'Thought Management',
        description: 'Task management and note taking tools that help ideas stay captured instead of scattered.',
        icon: <LayoutGrid className="h-6 w-6" />,
        accent: 'from-indigo-400/30 via-blue-400/10 to-transparent',
      },
      {
        name: 'Time Tracking',
        description: 'Stopwatch and time display tools for quick focus sessions, pacing, and visibility.',
        icon: <TimerReset className="h-6 w-6" />,
        accent: 'from-fuchsia-400/30 via-pink-400/10 to-transparent',
      },
      {
        name: 'Link Organization',
        description: 'Keep desktop apps and web links together in one place so your setup opens faster and stays organized.',
        icon: <LinkIcon className="h-6 w-6" />,
        accent: 'from-sky-400/30 via-cyan-400/10 to-transparent',
      },
      {
        name: 'Calculations',
        description: 'Quick calculation tools for day-to-day math without needing to break focus.',
        icon: <Sparkles className="h-6 w-6" />,
        accent: 'from-violet-400/30 via-purple-400/10 to-transparent',
      },
      {
        name: 'Customization',
        description: 'Themes and background patterns that make the app feel personal without turning the interface noisy.',
        icon: <SlidersHorizontal className="h-6 w-6" />,
        accent: 'from-amber-400/30 via-orange-400/10 to-transparent',
      },
      {
        name: 'Endless More',
        description: 'New widgets keep arriving based on real demand, so the product can grow around what people actually use.',
        icon: <PlusCircle className="h-6 w-6" />,
        accent: 'from-emerald-400/30 via-green-400/10 to-transparent',
      },
    ],
  },
];

const sectionViewport = { once: true, amount: 0.2 };

const FeaturesPage = () => {
  const prefersReducedMotion = useReducedMotion();

  const fadeUp = {
    hidden: { opacity: 0, y: prefersReducedMotion ? 0 : 26 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: prefersReducedMotion ? 0.2 : 0.65, ease: [0.22, 1, 0.36, 1] },
    },
  };

  const staggerGroup = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: prefersReducedMotion ? 0.04 : 0.08,
        delayChildren: prefersReducedMotion ? 0 : 0.05,
      },
    },
  };

  const cardHover = prefersReducedMotion
    ? {}
    : {
        y: -6,
        borderColor: 'rgba(96, 165, 250, 0.28)',
        transition: { duration: 0.22, ease: 'easeOut' },
      };

  return (
    <PageShell>
      <div className="space-y-10 py-4">
        <PageBackLink />

        {featureCategories.map((category) => (
          <motion.section
            key={category.title}
            className="mx-auto desktop-page-width-xl space-y-8"
            variants={staggerGroup}
            initial="hidden"
            whileInView="visible"
            viewport={sectionViewport}
          >
            <motion.div variants={fadeUp} className="max-w-3xl">
              <div className="text-sm uppercase tracking-[0.3em] text-blue-300">{category.title}</div>
              <h2 className="mt-3 text-3xl font-bold text-white sm:text-4xl">Built around the tools people actually come back to.</h2>
              <p className="mt-4 text-lg leading-relaxed text-gray-300">{category.description}</p>
            </motion.div>

            <motion.div variants={staggerGroup} className="grid gap-5 md:grid-cols-2 xl:grid-cols-3 2xl:gap-6">
              {category.features.map((feature) => (
                <motion.article
                  key={feature.name}
                  variants={fadeUp}
                  whileHover={cardHover}
                  className="relative h-full overflow-hidden rounded-[1.75rem] border border-white/10 bg-white/[0.04] p-6"
                >
                  <div aria-hidden="true" className={`absolute inset-0 bg-gradient-to-br ${feature.accent} opacity-80`} />
                  <div className="relative flex h-full flex-col">
                    <div className="flex items-start justify-between gap-4">
                      <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-black/25 text-white">
                        {feature.icon}
                      </div>
                      <div className="rounded-full border border-white/10 bg-black/20 px-3 py-1 text-[11px] uppercase tracking-[0.22em] text-gray-300">
                        Ready fast
                      </div>
                    </div>
                    <h3 className="mt-6 text-2xl font-semibold text-white">{feature.name}</h3>
                    <p className="mt-3 flex-1 leading-relaxed text-gray-300">{feature.description}</p>
                    <div className="mt-6 border-t border-white/10 pt-4 text-sm text-gray-400">
                      Designed to stay simple enough for everyday use.
                    </div>
                  </div>
                </motion.article>
              ))}
            </motion.div>
          </motion.section>
        ))}
      </div>
    </PageShell>
  );
};

export default FeaturesPage;
